import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  Chip,
  Alert,
  Tooltip,
} from '@mui/material';
import type { FieldSpec, ModelOption } from '../../types';
import { CostSlider } from '../common/CostSlider';
import { NumberInput } from '../common/NumberInput';
import { isVisible } from '../../utils/expr';

interface Props {
  spec: FieldSpec;
  value: unknown;
  onChange: (value: unknown) => void;
  params?: Record<string, unknown>;
  hints?: Record<string, unknown>;
  models?: { text?: ModelOption[]; voice?: ModelOption[] } | null;
}

// Resolve a dotted path against an object, e.g. "ecs.recommended_max".
function readPath(obj: Record<string, unknown> | undefined, path: string): unknown {
  if (!obj) return undefined;
  return path.split('.').reduce<unknown>((acc, k) => {
    if (acc && typeof acc === 'object' && k in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[k];
    }
    return undefined;
  }, obj);
}

// Render a tooltip body. Accepts either a plain string or
// { title: string, lines: string[] }.
function renderTooltipBody(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string') return v;
  if (typeof v === 'object' && 'lines' in (v as Record<string, unknown>)) {
    const obj = v as { title?: string; lines?: string[] };
    return (
      <Box>
        {obj.title && (
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {obj.title}
          </Typography>
        )}
        {obj.lines?.map((line, i) => (
          <Typography key={i} variant="caption" component="div">
            {line}
          </Typography>
        ))}
      </Box>
    );
  }
  return String(v);
}

export function FieldRenderer({ spec, value, onChange, params, hints, models }: Props) {
  // Visibility gate applies uniformly across all field types.
  if (params && !isVisible(spec.visible_when, params)) return null;

  if (spec.type === 'slider') {
    const multiplier = spec.multiplier ?? 1;
    const displayValue =
      typeof value === 'number' ? value * multiplier : spec.default * multiplier;
    return (
      <CostSlider
        label={spec.label}
        description={spec.description}
        value={displayValue}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        unit={spec.unit}
        warningWhenZero={spec.warning_when_zero ?? undefined}
        onChange={(v) => onChange(multiplier !== 1 ? v / multiplier : v)}
      />
    );
  }

  if (spec.type === 'number') {
    return (
      <NumberInput
        label={spec.label}
        value={typeof value === 'number' ? value : spec.default}
        min={spec.min}
        max={spec.max}
        step={spec.step}
        unit={spec.unit}
        onChange={onChange}
      />
    );
  }

  if (spec.type === 'select') {
    const refKey = spec.options_ref as 'text' | 'voice' | null;
    const modelList = refKey && models ? models[refKey] : undefined;
    const options = modelList
      ? modelList.map((m) => ({
          value: m.id,
          label: `${m.name} (${m.provider})`,
          description: '',
        }))
      : spec.options;

    return (
      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
        <InputLabel>{spec.label}</InputLabel>
        <Select
          value={typeof value === 'string' ? value : spec.default}
          label={spec.label}
          onChange={(e) => onChange(e.target.value)}
        >
          {options.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  if (spec.type === 'toggle') {
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          {spec.label}
        </Typography>
        {spec.description && (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {spec.description}
          </Typography>
        )}
        <ToggleButtonGroup
          exclusive
          value={typeof value === 'string' ? value : spec.default}
          onChange={(_, v) => v !== null && onChange(v)}
          size="small"
        >
          {spec.options.map((opt) => (
            <ToggleButton key={opt.value} value={opt.value}>
              {opt.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
    );
  }

  if (spec.type === 'derived') {
    const v = readPath(hints, spec.source);
    if (v === undefined || v === null || v === '') return null;
    const text = `${spec.label}: ${String(v)}`;
    const tooltipBody = spec.tooltip_source
      ? renderTooltipBody(readPath(hints, spec.tooltip_source))
      : null;

    if (spec.renderer === 'alert') {
      return (
        <Alert severity={spec.severity} sx={{ mb: 2 }}>
          {String(v)}
        </Alert>
      );
    }
    if (spec.renderer === 'tooltip') {
      return (
        <Tooltip title={tooltipBody ?? String(v)} arrow placement="right">
          <Chip
            label={spec.label}
            size="small"
            color={spec.severity === 'warning' ? 'warning' : 'default'}
            sx={{ mb: 1, cursor: 'help' }}
          />
        </Tooltip>
      );
    }
    if (spec.renderer === 'text') {
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 1, display: 'block' }}
        >
          {text}
        </Typography>
      );
    }
    // default: chip (with optional rich tooltip)
    const color =
      spec.severity === 'warning'
        ? 'warning'
        : spec.severity === 'error'
        ? 'error'
        : spec.severity === 'success'
        ? 'success'
        : 'info';
    const chip = (
      <Chip
        label={text}
        size="small"
        color={color}
        variant="outlined"
        sx={{ mb: 1, mr: 1, cursor: tooltipBody ? 'help' : 'default' }}
      />
    );
    return tooltipBody ? (
      <Tooltip title={tooltipBody} arrow placement="right">
        {chip}
      </Tooltip>
    ) : (
      chip
    );
  }

  return null;
}
